import React, { useState, useEffect } from 'react';
import { useApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { 
  Heart, Plus, Users, MapPin, Calendar, Clock, Star, CheckCircle, 
  Search, Sparkles, UserPlus, HandHeart, Award
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { toast } from 'sonner';

const STATUS_COLORS = {
  "Aberta": "bg-green-500",
  "Em Curso": "bg-blue-500",
  "Concluída": "bg-slate-500",
  "Cancelada": "bg-red-500"
};

const RECOMENDACAO_COLORS = {
  "Forte": "bg-green-500",
  "Moderada": "bg-yellow-500",
  "Fraca": "bg-red-500"
};

const CHART_COLORS = ['#0066CC', '#00AA66', '#F59E0B', '#EF4444', '#8B5CF6'];

const COMPETENCIAS_OPTIONS = [
  'Apoio Administrativo', 'Informática', 'Comunicação', 'Motorista', 'Logística',
  'Apoio Alimentar', 'Psicologia', 'Apoio Emocional', 'Escuta Ativa', 'Carpintaria',
  'Manutenção', 'Bricolage', 'Design Gráfico', 'Marketing Digital', 'Redes Sociais',
  'Contabilidade', 'Jurídico', 'Saúde', 'Educação', 'Tradução'
];

export default function VoluntariadoPage() {
  const api = useApi();
  const [dashboard, setDashboard] = useState(null);
  const [voluntarios, setVoluntarios] = useState([]);
  const [missoes, setMissoes] = useState([]);
  const [distritos, setDistritos] = useState([]);
  const [instituicoes, setInstituicoes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [voluntarioModalOpen, setVoluntarioModalOpen] = useState(false);
  const [missaoModalOpen, setMissaoModalOpen] = useState(false);
  const [matchingModalOpen, setMatchingModalOpen] = useState(false);
  const [candidaturaModalOpen, setCandidaturaModalOpen] = useState(false);
  const [selectedMissao, setSelectedMissao] = useState(null);
  const [matchingResults, setMatchingResults] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  
  // Forms
  const [voluntarioForm, setVoluntarioForm] = useState({
    nome: '',
    email: '',
    telefone: '',
    distrito_residencia_id: '',
    competencias: [],
    horas_mensais_disponiveis: 8
  });
  
  const [missaoForm, setMissaoForm] = useState({
    instituicao_id: '',
    titulo: '',
    descricao: '',
    tipo_missao: 'Presencial',
    competencias_necessarias: [],
    numero_voluntarios_necessarios: 1,
    data_inicio: '',
    horas_estimadas: 4,
    localizacao: ''
  });
  
  const [candidaturaForm, setCandidaturaForm] = useState({
    voluntario_id: '',
    mensagem: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const [dashData, volData, missData, distData, instData] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/voluntariado/dashboard`, { headers }).then(r => r.json()),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/voluntarios`, { headers }).then(r => r.json()),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/missoes`, { headers }).then(r => r.json()),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/distritos`, { headers }).then(r => r.json()),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/instituicoes`, { headers }).then(r => r.json())
      ]);
      setDashboard(dashData);
      setVoluntarios(Array.isArray(volData) ? volData : []);
      setMissoes(Array.isArray(missData) ? missData : []);
      setDistritos(Array.isArray(distData) ? distData : []);
      setInstituicoes(Array.isArray(instData) ? instData : []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVoluntario = async () => {
    if (!voluntarioForm.nome || !voluntarioForm.email || !voluntarioForm.distrito_residencia_id) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    setFormLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/voluntarios`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(voluntarioForm)
      });
      
      if (!response.ok) throw new Error('Erro ao criar voluntário');
      
      toast.success('Voluntário registado com sucesso!');
      setVoluntarioModalOpen(false);
      setVoluntarioForm({ nome: '', email: '', telefone: '', distrito_residencia_id: '', competencias: [], horas_mensais_disponiveis: 8 });
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao registar voluntário');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCreateMissao = async () => {
    if (!missaoForm.instituicao_id || !missaoForm.titulo || !missaoForm.data_inicio) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    setFormLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/missoes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(missaoForm)
      });
      
      if (!response.ok) throw new Error('Erro ao criar missão');
      
      toast.success('Missão criada com sucesso!');
      setMissaoModalOpen(false);
      setMissaoForm({ instituicao_id: '', titulo: '', descricao: '', tipo_missao: 'Presencial', competencias_necessarias: [], numero_voluntarios_necessarios: 1, data_inicio: '', horas_estimadas: 4, localizacao: '' });
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao criar missão');
    } finally {
      setFormLoading(false);
    }
  };

  const openMatching = async (missao) => {
    setSelectedMissao(missao);
    setMatchingModalOpen(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/missoes/${missao.id}/matching`);
      const data = await response.json();
      setMatchingResults(data);
    } catch (error) {
      console.error('Error fetching matching:', error);
      toast.error('Erro ao calcular matching');
    }
  };

  const handleCandidatura = async () => {
    if (!candidaturaForm.voluntario_id) {
      toast.error('Selecione um voluntário');
      return;
    }
    
    setFormLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/missoes/${selectedMissao.id}/candidatura`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(candidaturaForm)
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Erro ao candidatar');
      }
      
      toast.success('Candidatura submetida com sucesso!');
      setCandidaturaModalOpen(false);
      setCandidaturaForm({ voluntario_id: '', mensagem: '' });
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Erro ao submeter candidatura');
    } finally {
      setFormLoading(false);
    }
  };

  const confirmVoluntario = async (match) => {
    setCandidaturaForm({ voluntario_id: match.voluntario_id, mensagem: '' });
    setMatchingModalOpen(false);
    setCandidaturaModalOpen(true);
  };

  // Chart data
  const voluntariosPorDistrito = dashboard?.voluntarios_por_distrito 
    ? Object.entries(dashboard.voluntarios_por_distrito).map(([name, value]) => ({ name, value }))
    : [];

  const toggleCompetencia = (comp, formSetter, formData, field) => {
    const current = formData[field] || [];
    if (current.includes(comp)) {
      formSetter(prev => ({ ...prev, [field]: current.filter(c => c !== comp) }));
    } else {
      formSetter(prev => ({ ...prev, [field]: [...current, comp] }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="voluntariado-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="voluntariado-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Voluntariado</h1>
          <p className="text-slate-500 mt-1">Gestão de voluntários e missões com matching inteligente</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setVoluntarioModalOpen(true)}
            className="bg-[#0066CC] hover:bg-[#0052a3]"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Novo Voluntário
          </Button>
          <Button 
            onClick={() => setMissaoModalOpen(true)}
            variant="outline"
          >
            <HandHeart className="w-4 h-4 mr-2" />
            Nova Missão
          </Button>
        </div>
      </div>

      {/* Dashboard Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="card-metric">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Voluntários Ativos</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">
                  {dashboard?.total_voluntarios_ativos || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#0066CC]/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#0066CC]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-metric">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Horas Voluntariado</p>
                <p className="text-3xl font-bold text-[#00AA66] mt-2">
                  {dashboard?.total_horas_voluntariado?.toLocaleString('pt-PT') || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-[#00AA66]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-metric">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Missões Abertas</p>
                <p className="text-3xl font-bold text-purple-600 mt-2">
                  {dashboard?.missoes_abertas || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Heart className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-metric">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Missões Concluídas</p>
                <p className="text-3xl font-bold text-amber-600 mt-2">
                  {dashboard?.total_missoes_concluidas || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Award className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Voluntários por Distrito</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={voluntariosPorDistrito} layout="vertical" margin={{ left: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={75} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#0066CC" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Top Voluntários</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {voluntarios.slice(0, 5).map((vol, index) => (
                <div key={vol.id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                    index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-slate-400' : index === 2 ? 'bg-amber-700' : 'bg-slate-300'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">{vol.nome}</p>
                    <p className="text-xs text-slate-500">{vol.numero_missoes_concluidas} missões</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-[#0066CC]">{vol.total_horas_voluntariado}h</p>
                    <div className="flex items-center gap-1">
                      <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                      <span className="text-xs">{vol.classificacao_media}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Missões and Voluntários */}
      <Tabs defaultValue="missoes" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="missoes">Missões</TabsTrigger>
          <TabsTrigger value="voluntarios">Voluntários</TabsTrigger>
        </TabsList>

        <TabsContent value="missoes">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <HandHeart className="w-5 h-5 text-purple-500" />
                Missões de Voluntariado
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Missão</TableHead>
                    <TableHead>Instituição</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-center">Voluntários</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {missoes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                        Nenhuma missão registada
                      </TableCell>
                    </TableRow>
                  ) : (
                    missoes.map((missao) => (
                      <TableRow key={missao.id} className="hover:bg-slate-50">
                        <TableCell>
                          <p className="font-medium">{missao.titulo}</p>
                          <p className="text-xs text-slate-500 line-clamp-1">{missao.descricao}</p>
                        </TableCell>
                        <TableCell>{missao.instituicao_nome}</TableCell>
                        <TableCell><Badge variant="outline">{missao.tipo_missao}</Badge></TableCell>
                        <TableCell className="text-center">
                          <span className="font-bold text-[#0066CC]">{missao.numero_voluntarios_confirmados}</span>
                          <span className="text-slate-400">/{missao.numero_voluntarios_necessarios}</span>
                        </TableCell>
                        <TableCell>{missao.data_inicio}</TableCell>
                        <TableCell>
                          <Badge className={`${STATUS_COLORS[missao.status]} text-white`}>
                            {missao.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {missao.status === 'Aberta' && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openMatching(missao)}
                              className="text-[#0066CC]"
                            >
                              <Sparkles className="w-4 h-4 mr-1" />
                              Matching IA
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="voluntarios">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Users className="w-5 h-5 text-[#0066CC]" />
                Voluntários Registados
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Nome</TableHead>
                    <TableHead>Distrito</TableHead>
                    <TableHead>Competências</TableHead>
                    <TableHead className="text-center">Horas/Mês</TableHead>
                    <TableHead className="text-center">Missões</TableHead>
                    <TableHead className="text-center">Rating</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {voluntarios.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                        Nenhum voluntário registado
                      </TableCell>
                    </TableRow>
                  ) : (
                    voluntarios.map((vol) => (
                      <TableRow key={vol.id} className="hover:bg-slate-50">
                        <TableCell>
                          <p className="font-medium">{vol.nome}</p>
                          <p className="text-xs text-slate-500">{vol.email}</p>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-slate-400" />
                            {vol.distrito_nome}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(vol.competencias || []).slice(0, 2).map(c => (
                              <Badge key={c} variant="outline" className="text-xs">{c}</Badge>
                            ))}
                            {(vol.competencias || []).length > 2 && (
                              <Badge variant="outline" className="text-xs">+{vol.competencias.length - 2}</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">{vol.horas_mensais_disponiveis}h</TableCell>
                        <TableCell className="text-center font-bold">{vol.numero_missoes_concluidas}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                            {vol.classificacao_media}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={vol.status === 'ativo' ? 'bg-green-500' : 'bg-slate-500'}>
                            {vol.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Voluntário Modal */}
      <Dialog open={voluntarioModalOpen} onOpenChange={setVoluntarioModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Novo Voluntário</DialogTitle>
            <DialogDescription>Registar um novo voluntário na plataforma</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nome *</Label>
                <Input
                  value={voluntarioForm.nome}
                  onChange={(e) => setVoluntarioForm(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="Nome completo"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={voluntarioForm.email}
                  onChange={(e) => setVoluntarioForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@exemplo.pt"
                />
              </div>
              <div>
                <Label>Telefone</Label>
                <Input
                  value={voluntarioForm.telefone}
                  onChange={(e) => setVoluntarioForm(prev => ({ ...prev, telefone: e.target.value }))}
                  placeholder="912345678"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Distrito *</Label>
                <Select 
                  value={voluntarioForm.distrito_residencia_id} 
                  onValueChange={(v) => setVoluntarioForm(prev => ({ ...prev, distrito_residencia_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {distritos.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Horas disponíveis/mês</Label>
                <Input
                  type="number"
                  min="1"
                  value={voluntarioForm.horas_mensais_disponiveis}
                  onChange={(e) => setVoluntarioForm(prev => ({ ...prev, horas_mensais_disponiveis: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>
            
            <div>
              <Label className="mb-2 block">Competências</Label>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border rounded-lg">
                {COMPETENCIAS_OPTIONS.map(comp => (
                  <Badge
                    key={comp}
                    variant={voluntarioForm.competencias.includes(comp) ? 'default' : 'outline'}
                    className={`cursor-pointer ${voluntarioForm.competencias.includes(comp) ? 'bg-[#0066CC]' : ''}`}
                    onClick={() => toggleCompetencia(comp, setVoluntarioForm, voluntarioForm, 'competencias')}
                  >
                    {comp}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoluntarioModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateVoluntario} className="bg-[#0066CC] hover:bg-[#0052a3]" disabled={formLoading}>
              {formLoading ? 'A registar...' : 'Registar Voluntário'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Missão Modal */}
      <Dialog open={missaoModalOpen} onOpenChange={setMissaoModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nova Missão</DialogTitle>
            <DialogDescription>Criar uma nova missão de voluntariado</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Instituição *</Label>
              <Select 
                value={missaoForm.instituicao_id} 
                onValueChange={(v) => setMissaoForm(prev => ({ ...prev, instituicao_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {instituicoes.map(i => (
                    <SelectItem key={i.id} value={i.id}>{i.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Título *</Label>
              <Input
                value={missaoForm.titulo}
                onChange={(e) => setMissaoForm(prev => ({ ...prev, titulo: e.target.value }))}
                placeholder="Título da missão"
              />
            </div>
            
            <div>
              <Label>Descrição</Label>
              <Textarea
                value={missaoForm.descricao}
                onChange={(e) => setMissaoForm(prev => ({ ...prev, descricao: e.target.value }))}
                placeholder="Descreva a missão..."
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Tipo</Label>
                <Select 
                  value={missaoForm.tipo_missao} 
                  onValueChange={(v) => setMissaoForm(prev => ({ ...prev, tipo_missao: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Presencial">Presencial</SelectItem>
                    <SelectItem value="Remoto">Remoto</SelectItem>
                    <SelectItem value="Híbrido">Híbrido</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Voluntários</Label>
                <Input
                  type="number"
                  min="1"
                  value={missaoForm.numero_voluntarios_necessarios}
                  onChange={(e) => setMissaoForm(prev => ({ ...prev, numero_voluntarios_necessarios: parseInt(e.target.value) || 1 }))}
                />
              </div>
              <div>
                <Label>Horas</Label>
                <Input
                  type="number"
                  min="1"
                  value={missaoForm.horas_estimadas}
                  onChange={(e) => setMissaoForm(prev => ({ ...prev, horas_estimadas: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Início *</Label>
                <Input
                  type="date"
                  value={missaoForm.data_inicio}
                  onChange={(e) => setMissaoForm(prev => ({ ...prev, data_inicio: e.target.value }))}
                />
              </div>
              <div>
                <Label>Localização</Label>
                <Input
                  value={missaoForm.localizacao}
                  onChange={(e) => setMissaoForm(prev => ({ ...prev, localizacao: e.target.value }))}
                  placeholder="Cidade/Local"
                />
              </div>
            </div>
            
            <div>
              <Label className="mb-2 block">Competências Necessárias</Label>
              <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto p-2 border rounded-lg">
                {COMPETENCIAS_OPTIONS.map(comp => (
                  <Badge
                    key={comp}
                    variant={missaoForm.competencias_necessarias.includes(comp) ? 'default' : 'outline'}
                    className={`cursor-pointer ${missaoForm.competencias_necessarias.includes(comp) ? 'bg-[#0066CC]' : ''}`}
                    onClick={() => toggleCompetencia(comp, setMissaoForm, missaoForm, 'competencias_necessarias')}
                  >
                    {comp}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setMissaoModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateMissao} className="bg-[#0066CC] hover:bg-[#0052a3]" disabled={formLoading}>
              {formLoading ? 'A criar...' : 'Criar Missão'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Matching Modal */}
      <Dialog open={matchingModalOpen} onOpenChange={setMatchingModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Matching IA - Voluntários Recomendados
            </DialogTitle>
            <DialogDescription>
              {selectedMissao?.titulo}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {matchingResults.length === 0 ? (
              <p className="text-center text-slate-500 py-8">A calcular compatibilidade...</p>
            ) : (
              matchingResults.map((match) => (
                <div key={match.voluntario_id} className="border rounded-lg p-4 hover:border-[#0066CC] transition-colors">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-lg">{match.voluntario_nome}</p>
                      <p className="text-sm text-slate-500 mt-1">{match.explicacao}</p>
                    </div>
                    <Badge className={`${RECOMENDACAO_COLORS[match.recomendacao_ia]} text-white`}>
                      {match.recomendacao_ia}
                    </Badge>
                  </div>
                  
                  <div className="mt-4 grid grid-cols-4 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-[#0066CC]">{match.score_compatibilidade}</p>
                      <p className="text-xs text-slate-500">Total</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-semibold text-slate-700">{match.score_competencias}</p>
                      <p className="text-xs text-slate-500">Competências</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-semibold text-slate-700">{match.score_disponibilidade}</p>
                      <p className="text-xs text-slate-500">Disponibilidade</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-semibold text-slate-700">{match.score_localizacao}</p>
                      <p className="text-xs text-slate-500">Localização</p>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <Progress value={match.score_compatibilidade} className="h-2" />
                  </div>
                  
                  <div className="mt-4 flex justify-end">
                    <Button 
                      size="sm" 
                      className="bg-[#00AA66] hover:bg-[#008855]"
                      onClick={() => confirmVoluntario(match)}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Convidar
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Candidatura Modal */}
      <Dialog open={candidaturaModalOpen} onOpenChange={setCandidaturaModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Candidatura</DialogTitle>
            <DialogDescription>
              Registar candidatura do voluntário para a missão
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Voluntário</Label>
              <Select 
                value={candidaturaForm.voluntario_id} 
                onValueChange={(v) => setCandidaturaForm(prev => ({ ...prev, voluntario_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {voluntarios.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Mensagem (opcional)</Label>
              <Textarea
                value={candidaturaForm.mensagem}
                onChange={(e) => setCandidaturaForm(prev => ({ ...prev, mensagem: e.target.value }))}
                placeholder="Mensagem de motivação..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCandidaturaModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleCandidatura} className="bg-[#00AA66] hover:bg-[#008855]" disabled={formLoading}>
              {formLoading ? 'A submeter...' : 'Confirmar Candidatura'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
